import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import tesoreriaFlujoFondosService, {
  FlujoFondosPresupuestado,
} from '../../../services/tesoreriaFlujoFondosService';
import { formatCurrency } from '../../../utils/formatters';

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const FlujoFondosPresupuestadosDialog: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<FlujoFondosPresupuestado[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tipo, setTipo] = useState<'I' | 'E'>('I');
  const [fecha, setFecha] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [importe, setImporte] = useState('');
  const [concepto, setConcepto] = useState('');
  const [notas, setNotas] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tesoreriaFlujoFondosService.listPresupuestados({ soloActivos: true });
      setItems(data);
    } catch {
      enqueueSnackbar('No se pudieron cargar los presupuestados', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const handleCreate = async () => {
    const valor = Number(importe);
    if (!concepto.trim()) {
      enqueueSnackbar('Ingresá un concepto', { variant: 'warning' });
      return;
    }
    if (!valor || valor <= 0) {
      enqueueSnackbar('Ingresá un importe válido', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await tesoreriaFlujoFondosService.createPresupuestado({
        tipo,
        fecha,
        importe: valor,
        concepto: concepto.trim(),
        notas: notas.trim() || undefined,
      });
      enqueueSnackbar('Flujo presupuestado creado', { variant: 'success' });
      setConcepto('');
      setNotas('');
      setImporte('');
      await load();
      onChanged();
    } catch {
      enqueueSnackbar('No se pudo crear el flujo', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await tesoreriaFlujoFondosService.deletePresupuestado(id);
      enqueueSnackbar('Flujo eliminado', { variant: 'success' });
      await load();
      onChanged();
    } catch {
      enqueueSnackbar('No se pudo eliminar', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Flujos presupuestados</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
          Alta rápida
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Tipo</InputLabel>
            <Select label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value as 'I' | 'E')}>
              <MenuItem value="I">Ingreso</MenuItem>
              <MenuItem value="E">Egreso</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Fecha"
            type="date"
            size="small"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Importe"
            type="number"
            size="small"
            value={importe}
            onChange={(e) => setImporte(e.target.value)}
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="Concepto"
            size="small"
            fullWidth
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
          />
        </Stack>
        <TextField
          label="Notas (opcional)"
          size="small"
          fullWidth
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button variant="contained" onClick={handleCreate} disabled={saving}>
          Agregar
        </Button>

        <Typography variant="subtitle2" fontWeight={600} mt={3} mb={1}>
          Activos
        </Typography>
        <Box sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Concepto</TableCell>
                <TableCell align="right">Importe</TableCell>
                <TableCell align="center" width={56} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{format(new Date(item.fecha), 'dd/MM/yyyy')}</TableCell>
                  <TableCell>{item.tipo === 'E' ? 'Egreso' : 'Ingreso'}</TableCell>
                  <TableCell>{item.concepto}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: item.tipo === 'E' ? '#c62828' : '#2e7d32', fontWeight: 600 }}
                  >
                    {formatCurrency(item.importe)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleDelete(item.id)} aria-label="Eliminar">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      Todavía no hay flujos presupuestados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlujoFondosPresupuestadosDialog;
